/**
 * Tenant Settings Page
 * Tenant-aware configuration settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { Business as TenantIcon } from '@mui/icons-material';
import { apiGet, apiPut } from '../../utils/apiClient';
import { cachedApiGet } from '../../utils/refDataCache';
import logger from '../../utils/logger';
import { useTenant } from '../../contexts/TenantContext';
import LabeledSwitch from '../../components/common/LabeledSwitch';
import ToggleGroup from '../../components/common/ToggleGroup';

// Settings arrive as strings ('true'/'false') from the key/value store, or booleans once toggled.
const isOn = (value, fallback) => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return fallback;
};

function TenantSettingsPage() {
  const { tenant, reload: reloadTenant, isModuleEnabled } = useTenant();
  const [settings, setSettings] = useState({
    systemName: '',
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    defaultCurrency: 'ZAR',
    dateFormat: 'YYYY-MM-DD',
    timezone: 'Africa/Johannesburg',
    // Backdating: each rule is an on/off switch plus a day limit. An unsaved switch reads as ON
    // (backdating was always allowed before the switch existed). The contract limit was never
    // enforced before, so blank = no limit (services/backdatePolicy.js on the API).
    backdate_enabled_contract: true,
    max_backdate_days_contract: '',
    backdate_enabled_historical: true,
    max_backdate_days_historical: '3650',
    backdate_enabled_rate_change: true,
    max_backdate_days_rate_change: '30',
    // Matured contracts: default OFF — a contract locks at maturity unless a tenant opts in.
    matured_amendment_enabled: false,
    matured_amendment_window_days: '30',
    maturity_grace_period_days: '30',
    contract_require_activation: false,
    contract_require_likelihood_to_roll: false,
    auto_create_capital_receipt_on_approve: false,
    // Feature 30. Default false: the fee stays a single upfront figure unless a tenant opts in.
    enable_broker_fee_scheduling: false,
    // Notes module (routes/notes.js NOTE_SETTINGS). Blank = the API's default.
    notes_spread_standard: '0.5',
    notes_spread_staff: '',
    notes_reference_prefix: 'NTE',
    notes_require_approval: true,
    notes_company_wht_exempt: true,
    allow_delete_historic_contracts: false,
    allow_historical_merge: false,
    allow_finalisation_writeoff: false,
    max_finalisation_writeoff: '0.00',
    finalisation_writeoff_dr_account: '',
    // T3-169: over-payment tolerance, expressed in DAYS OF INTEREST ACCRUAL so it scales with the
    // contract. Separate per path: matching settles one line, finalisation a whole contract.
    payment_match_overpay_days: '0',
    finalisation_overpay_days: '0',
    allow_historical_balance_override: false,
    // T3-162: DISPLAY only, and no invented default — unset means the screens' own display default.
    rounding_precision: '',
    reconstruct_historic_rates_variable: false,
    reconstruct_historic_rates_floating: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Distinct currency codes from the operating tenant's active countries. This page
  // edits the tenant resolved by useTenant() (there is no tenant switcher here), so
  // the tenant-scoped refdata call and the master row being written agree.
  const [currencyOptions, setCurrencyOptions] = useState([]);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);

      if (tenant?.id) {
        // Load tenant-specific settings
        const response = await apiGet(`/tenants/${tenant.id}/settings`);
        if (response.success && response.data) {
          // Convert array of settings to object
          const settingsObj = {};
          response.data.forEach(s => {
            settingsObj[s.setting_key] = s.setting_value;
          });
          setSettings(prev => ({ ...prev, ...settingsObj, systemName: tenant.systemName || '' }));
        }
      } else {
        // Load global settings (fallback)
        const response = await apiGet('/admin/settings');
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      }
    } catch (error) {
      logger.error('Error loading settings', { message: error.message, status: error.response?.status });
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadCurrencyOptions = useCallback(async () => {
    try {
      const countriesData = await cachedApiGet('/refdata/countries?active_only=true');
      const countries = Array.isArray(countriesData) ? countriesData
        : (countriesData?.data || countriesData?.rows || []);
      // Many countries share a currency code (e.g. ZAR), so the Set is required.
      setCurrencyOptions([...new Set(countries.map(c => c.currency_code).filter(Boolean))].sort());
    } catch (error) {
      // A failed lookup must not blank the field — the stored value still renders via
      // the fallback MenuItem, so surface the failure in logs and leave the list empty.
      logger.error('Error loading currency options', { message: error.message, status: error.response?.status });
    }
  }, []);

  useEffect(() => {
    loadCurrencyOptions();
  }, [loadCurrencyOptions]);

  const handleSave = async () => {
    // T3-158: note user references are <3-letter prefix><DDMMYYYY>; a bad default would make every
    // note without an issuer prefix fail to save, so refuse it here rather than at note time.
    if (tenant?.id && isModuleEnabled('notes')
        && !/^[A-Z]{3}$/.test(String(settings.notes_reference_prefix || ''))) {
      setMessage({ type: 'error', text: 'The default notes reference prefix must be exactly 3 letters (e.g. NTE)' });
      return;
    }
    try {
      setSaving(true);
      setMessage(null);

      if (tenant?.id) {
        // Save tenant-specific settings
        await apiPut(`/tenants/${tenant.id}/settings`, {
          systemName: settings.systemName,
          companyName: settings.companyName,
          companyAddress: settings.companyAddress,
          companyPhone: settings.companyPhone,
          companyEmail: settings.companyEmail,
          defaultCurrency: settings.defaultCurrency,
          dateFormat: settings.dateFormat,
          timezone: settings.timezone,
          backdate_enabled_contract: isOn(settings.backdate_enabled_contract, true),
          max_backdate_days_contract: settings.max_backdate_days_contract,
          backdate_enabled_historical: isOn(settings.backdate_enabled_historical, true),
          max_backdate_days_historical: settings.max_backdate_days_historical,
          backdate_enabled_rate_change: isOn(settings.backdate_enabled_rate_change, true),
          max_backdate_days_rate_change: settings.max_backdate_days_rate_change,
          matured_amendment_enabled: isOn(settings.matured_amendment_enabled, false),
          matured_amendment_window_days: settings.matured_amendment_window_days,
          maturity_grace_period_days: settings.maturity_grace_period_days,
          contract_require_activation: settings.contract_require_activation,
          contract_require_likelihood_to_roll: settings.contract_require_likelihood_to_roll,
          auto_create_capital_receipt_on_approve: settings.auto_create_capital_receipt_on_approve,
          // ⚠ A key omitted HERE renders and toggles but never persists — the classic failure
          // mode on this screen, since the payload is a hand-listed literal, not derived state.
          enable_broker_fee_scheduling: settings.enable_broker_fee_scheduling,
          notes_spread_standard: settings.notes_spread_standard,
          notes_spread_staff: settings.notes_spread_staff,
          notes_reference_prefix: settings.notes_reference_prefix,
          notes_require_approval: isOn(settings.notes_require_approval, true),
          notes_company_wht_exempt: isOn(settings.notes_company_wht_exempt, true),
          allow_delete_historic_contracts: settings.allow_delete_historic_contracts,
          allow_historical_merge: settings.allow_historical_merge,
          allow_finalisation_writeoff: settings.allow_finalisation_writeoff,
          max_finalisation_writeoff: settings.max_finalisation_writeoff,
          finalisation_writeoff_dr_account: settings.finalisation_writeoff_dr_account,
          payment_match_overpay_days: settings.payment_match_overpay_days,
          finalisation_overpay_days: settings.finalisation_overpay_days,
          allow_historical_balance_override: settings.allow_historical_balance_override,
          rounding_precision: settings.rounding_precision,
          reconstruct_historic_rates_variable: settings.reconstruct_historic_rates_variable,
          reconstruct_historic_rates_floating: settings.reconstruct_historic_rates_floating,
        });
        setMessage({ type: 'success', text: `Settings saved for tenant: ${tenant.name}` });
        // Refresh tenant context to update the system name in the navbar
        reloadTenant();
      } else {
        // Save global settings
        await apiPut('/admin/settings', settings);
        setMessage({ type: 'success', text: 'Global settings saved successfully' });
      }
    } catch (error) {
      logger.error('Error saving settings', { message: error.message, status: error.response?.status });
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Tenant Settings</Typography>
        {tenant && (
          <Chip
            icon={<TenantIcon />}
            label={`Tenant: ${tenant.name}`}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {tenant
          ? `Configure settings for ${tenant.name}. These settings apply to all users in this tenant.`
          : 'Configure global system settings. These apply when no tenant context is available.'}
      </Typography>
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Branding
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={12}>
            <TextField
              fullWidth
              label="System Name"
              value={settings.systemName}
              onChange={handleChange('systemName')}
              helperText="This name appears in the header bar for all users"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Company Information
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Name"
              value={settings.companyName}
              onChange={handleChange('companyName')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Email"
              value={settings.companyEmail}
              onChange={handleChange('companyEmail')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Phone"
              value={settings.companyPhone}
              onChange={handleChange('companyPhone')}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Company Address"
              value={settings.companyAddress}
              onChange={handleChange('companyAddress')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Currency
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <FormControl fullWidth>
              <InputLabel id="default-currency-label">Base / Default Currency</InputLabel>
              <Select
                labelId="default-currency-label"
                label="Base / Default Currency"
                value={settings.defaultCurrency || ''}
                onChange={handleChange('defaultCurrency')}
              >
                {currencyOptions.map((code) => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
                {/* A previously-saved code with no active country row (or a legacy
                    free-text typo) must still render — otherwise the field appears
                    blank and saving would silently rewrite the stored value. */}
                {settings.defaultCurrency && !currencyOptions.includes(settings.defaultCurrency) && (
                  <MenuItem value={settings.defaultCurrency}>
                    {settings.defaultCurrency} (not in reference data)
                  </MenuItem>
                )}
              </Select>
              <FormHelperText>
                Reporting/display default and the FX base currency for this tenant. Changing this
                alters which exchange-rate pairs are fetched daily; rates already fetched against
                the previous base are not converted. Sourced from the currencies on active countries
                in Reference Data.
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Display Settings
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Date Format"
              value={settings.dateFormat}
              onChange={handleChange('dateFormat')}
              helperText="e.g., YYYY-MM-DD, DD/MM/YYYY"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Autocomplete
              freeSolo
              options={[
                'Africa/Johannesburg',
                'Africa/Nairobi',
                'Africa/Lagos',
                'Africa/Cairo',
                'Africa/Windhoek',
                'Africa/Harare',
                'Europe/London',
                'Europe/Paris',
                'America/New_York',
                'America/Chicago',
                'America/Los_Angeles',
                'Asia/Dubai',
                'Asia/Singapore',
                'Asia/Tokyo',
                'Australia/Sydney',
                'Pacific/Auckland',
                'UTC',
              ]}
              value={settings.timezone || 'Africa/Johannesburg'}
              onChange={(e, newValue) => setSettings(prev => ({ ...prev, timezone: newValue || 'Africa/Johannesburg' }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Timezone"
                  helperText="IANA timezone for business day calculations (e.g., Africa/Johannesburg)"
                />
              )}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Features
        </Typography>
        <ToggleGroup sx={{ mb: 3 }}>
          <LabeledSwitch
            label="Audit Logging"
            caption="All changes to contracts, payments, and settings are logged for compliance. Audit logging is always on and cannot be disabled."
            checked
            disabled
          />
        </ToggleGroup>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Business Rules
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <LabeledSwitch
              label="Allow Contract Backdating"
              caption="When off, a new contract's deposit date must be today."
              checked={isOn(settings.backdate_enabled_contract, true)}
              onChange={handleChange('backdate_enabled_contract')}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Max Backdate Days (Contracts)"
              type="number"
              value={settings.max_backdate_days_contract ?? ''}
              onChange={handleChange('max_backdate_days_contract')}
              disabled={!isOn(settings.backdate_enabled_contract, true)}
              placeholder="No limit"
              helperText="Maximum number of days in the past for contract deposit dates. Leave blank for no limit."
              inputProps={{ min: 0, max: 7300 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <LabeledSwitch
              label="Allow Historical Contract Backdating"
              caption="When off, historical (pre-go-live) contracts cannot be created."
              checked={isOn(settings.backdate_enabled_historical, true)}
              onChange={handleChange('backdate_enabled_historical')}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Max Backdate Days (Historical Contracts)"
              type="number"
              value={settings.max_backdate_days_historical}
              onChange={handleChange('max_backdate_days_historical')}
              disabled={!isOn(settings.backdate_enabled_historical, true)}
              helperText="Maximum days in the past for pre-go-live historical contract start dates"
              inputProps={{ min: 0, max: 7300 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <LabeledSwitch
              label="Allow Rate Change Backdating"
              caption="When off, every rate change (bulk, reference-data and per-contract) must take effect today or later."
              checked={isOn(settings.backdate_enabled_rate_change, true)}
              onChange={handleChange('backdate_enabled_rate_change')}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Max Backdate Days (Rate Changes)"
              type="number"
              value={settings.max_backdate_days_rate_change}
              onChange={handleChange('max_backdate_days_rate_change')}
              disabled={!isOn(settings.backdate_enabled_rate_change, true)}
              helperText="Maximum number of days in the past for rate change effective dates"
              inputProps={{ min: 0, max: 7300 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <LabeledSwitch
              label="Allow Amendments on Matured Contracts"
              caption="When on, a matured contract that has not been finalised can still take capital, interest, rate and fee amendments for the number of days below, so missed changes can be processed before finalisation. Amendment dates must still fall on or before maturity."
              checked={isOn(settings.matured_amendment_enabled, false)}
              onChange={handleChange('matured_amendment_enabled')}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Matured Amendment Window (days after maturity)"
              type="number"
              value={settings.matured_amendment_window_days}
              onChange={handleChange('matured_amendment_window_days')}
              disabled={!isOn(settings.matured_amendment_enabled, false)}
              helperText="How many days after its maturity date a contract can still be amended"
              inputProps={{ min: 0, max: 3650 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Maturity Grace Period (days)"
              type="number"
              value={settings.maturity_grace_period_days}
              onChange={handleChange('maturity_grace_period_days')}
              helperText="Days after maturity during which an 'Action Required' banner is shown on the contract"
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <TextField
              fullWidth
              label="Display decimals (screens & PDFs)"
              type="number"
              value={settings.rounding_precision}
              onChange={handleChange('rounding_precision')}
              helperText="How many decimals amounts are SHOWN with. Calculations do not use this — they round at each contract's calculation profile precision"
              inputProps={{ min: 0, max: 6 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Contract Workflow
        </Typography>
        <ToggleGroup sx={{ mb: 3 }}>
          <LabeledSwitch
            label="Require Separate Activation Step"
            caption="When enabled, approved contracts must be separately activated before they become active and the amortization schedule is generated. When disabled, contracts go directly to Active status upon approval."
            checked={settings.contract_require_activation === true || settings.contract_require_activation === 'true'}
            onChange={handleChange('contract_require_activation')}
          />
          <LabeledSwitch
            label="Require Likelihood to Roll"
            caption="When enabled, users must select a Likelihood to Roll value for every contract (and for each investor on multi-investor contracts) before the contract wizard can proceed."
            checked={settings.contract_require_likelihood_to_roll === true || settings.contract_require_likelihood_to_roll === 'true'}
            onChange={handleChange('contract_require_likelihood_to_roll')}
          />
          <LabeledSwitch
            label="Auto-Create Capital Receipt on Approval"
            caption="When enabled, approving a contract that has a Funds Received Date automatically creates the matching capital receipt against the Capital Drawdown. When disabled (default), the incoming capital flow must be matched manually on the Payment Matching screen."
            checked={settings.auto_create_capital_receipt_on_approve === true || settings.auto_create_capital_receipt_on_approve === 'true'}
            onChange={handleChange('auto_create_capital_receipt_on_approve')}
          />
          <LabeledSwitch
            label="Enable Broker Fee Scheduling"
            caption="When enabled, a broker fee can be accrued over the contract term and settled on its own payment cadence, and the fee scheduling controls appear in the contract wizard's broker dialog. When disabled (default), a broker fee stays a single upfront figure and no fee schedule is ever generated."
            checked={settings.enable_broker_fee_scheduling === true || settings.enable_broker_fee_scheduling === 'true'}
            onChange={handleChange('enable_broker_fee_scheduling')}
          />
          <LabeledSwitch
            label="Allow Deletion of Historical Contracts"
            caption="When enabled, administrators can permanently delete a contract that was entered as a historical (pre-go-live) record. This is irreversible and is blocked if the historical contract is referenced as the rolled-from source by another contract. When disabled (default), historical contracts cannot be deleted."
            checked={settings.allow_delete_historic_contracts === true || settings.allow_delete_historic_contracts === 'true'}
            onChange={handleChange('allow_delete_historic_contracts')}
          />
          <LabeledSwitch
            label="Allow Merging of Historical Contracts"
            caption="When enabled, administrators can merge one historical (pre-go-live) contract into another — repointing its rollover references and investors onto the survivor and hiding the merged record. Use to consolidate duplicate take-on stubs. When disabled (default), historical contracts cannot be merged."
            checked={settings.allow_historical_merge === true || settings.allow_historical_merge === 'true'}
            onChange={handleChange('allow_historical_merge')}
          />
          <LabeledSwitch
            label="Allow Amendment of Historical Contracts"
            caption="When enabled, administrators can amend a historical (pre-go-live) contract's allocatable rollover balance and/or external reference — e.g. to correct a take-on capture error — with a mandatory reason recorded in the audit trail. When disabled (default), historical contracts cannot be amended."
            checked={settings.allow_historical_balance_override === true || settings.allow_historical_balance_override === 'true'}
            onChange={handleChange('allow_historical_balance_override')}
          />
        </ToggleGroup>

        {isModuleEnabled('notes') && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A note leg&apos;s net rate is the gross rate less a commission spread (the company&apos;s income). The spread
              is taken from the investor&apos;s commission override if set, otherwise the staff or standard spread below,
              and is fixed on the leg when it is submitted &mdash; changing these later never alters a submitted leg.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth type="number" label="Standard commission spread (%)"
                  value={settings.notes_spread_standard} onChange={handleChange('notes_spread_standard')}
                  helperText="Gross rate minus net rate for non-staff investors (e.g. 0.5)"
                  inputProps={{ min: 0, step: '0.0001' }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth type="number" label="Staff commission spread (%)"
                  value={settings.notes_spread_staff} onChange={handleChange('notes_spread_staff')}
                  helperText="For investors flagged as staff. Blank = same as standard"
                  inputProps={{ min: 0, step: '0.0001' }} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth label="Default reference prefix"
                  value={settings.notes_reference_prefix || ''}
                  onChange={(e) => setSettings((prev) => ({
                    ...prev, notes_reference_prefix: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3) }))}
                  error={!/^[A-Z]{3}$/.test(String(settings.notes_reference_prefix || ''))}
                  inputProps={{ maxLength: 3 }}
                  helperText="3 letters. Used when the note's issuer has no Notes reference prefix of its own (Stakeholders → Issuer). Reference = prefix + maturity date, e.g. NTE15052026" />
              </Grid>
            </Grid>
            <ToggleGroup sx={{ mb: 3 }}>
              <LabeledSwitch
                label="Require Approval for Notes"
                caption="When enabled (default), each submitted investor leg and each maturity payout must be approved by a different user with Notes approve permission. When disabled, legs become Active and payouts Paid as soon as they are submitted."
                checked={isOn(settings.notes_require_approval, true)}
                onChange={handleChange('notes_require_approval')}
              />
              <LabeledSwitch
                label="Companies Exempt from WHT"
                caption="When enabled (default), no withholding tax is deducted on note legs held by non-individual investors (companies, funds, banks, trusts). When disabled, every investor's own WHT rate applies."
                checked={isOn(settings.notes_company_wht_exempt, true)}
                onChange={handleChange('notes_company_wht_exempt')}
              />
            </ToggleGroup>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Contract Finalisation &mdash; Write-Off
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When a contract is finalised, a part-paid line can be left with a small residual &mdash; typically
          rounding carried over from a legacy system, or a short remittance the counterparty considers
          settled. A residual at or below the maximum is written off and recorded against the contract
          with the limit that authorised it. Above the maximum it is left in full, never partly written
          off, and lines that received no payment at all are never written off.
        </Typography>
        {/* Toggle and its two settings on ONE row: the switch reads as the master control and the two
            short values — a money amount and an account code — sit beside it rather than each taking
            half the page width on a second row. */}
        <ToggleGroup>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 320px', minWidth: 280 }}>
              <LabeledSwitch
                label="Allow Write-Off at Finalisation"
                caption="Disabled by default, in which case residuals are always carried in full. Every write-off is recorded against the contract and in the audit trail."
                checked={settings.allow_finalisation_writeoff === true || settings.allow_finalisation_writeoff === 'true'}
                onChange={handleChange('allow_finalisation_writeoff')}
              />
            </Box>
            <TextField
              size="small"
              label="Maximum Write-Off"
              type="number"
              value={settings.max_finalisation_writeoff}
              onChange={handleChange('max_finalisation_writeoff')}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Contract currency. 0.00 disables."
              sx={{ width: 220, flex: '0 0 auto' }}
            />
            <TextField
              size="small"
              label="Write-Off Expense Account"
              value={settings.finalisation_writeoff_dr_account}
              onChange={handleChange('finalisation_writeoff_dr_account')}
              helperText="Debit account. Credit is derived from the product."
              sx={{ width: 220, flex: '0 0 auto' }}
            />
            {/* T3-169. Expressed in DAYS rather than an amount because the reason a payment
                legitimately exceeds its schedule is settlement TIMING — cash landing a day or two
                late carries the extra accrual. A flat figure is either too tight for a large
                contract or meaningless for a small one; days scale with the contract by
                construction. Two settings, because matching settles one line while finalisation
                settles a whole contract: the same number of days is a very different amount. */}
            <TextField
              size="small"
              label="Payment Over-Payment Tolerance"
              type="number"
              value={settings.payment_match_overpay_days}
              onChange={handleChange('payment_match_overpay_days')}
              inputProps={{ min: 0, step: 1 }}
              helperText="Days of interest. 0 requires an exact match."
              sx={{ width: 220, flex: '0 0 auto' }}
            />
            <TextField
              size="small"
              label="Finalisation Over-Payment Tolerance"
              type="number"
              value={settings.finalisation_overpay_days}
              onChange={handleChange('finalisation_overpay_days')}
              inputProps={{ min: 0, step: 1 }}
              helperText="Days of interest. 0 requires an exact allocation."
              sx={{ width: 220, flex: '0 0 auto' }}
            />
          </Box>
        </ToggleGroup>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Amortization &mdash; Historic Rate Reconstruction
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For onboarding active linked/floating-rate contracts that started before go-live. When enabled,
          generating (or regenerating) the amortization schedule rebuilds interest period-by-period using the
          benchmark rate that was in force on each period&apos;s date (benchmark rate + the contract&apos;s spread),
          instead of applying the current rate across the whole schedule. Only affects benchmark-linked contracts;
          leave off once historic data capture is complete.
        </Typography>
        <ToggleGroup sx={{ mb: 3 }}>
          <LabeledSwitch
            label="Reconstruct Historic Rates — Linked (Variable) Contracts"
            caption="When enabled, full schedule generation for Linked (Variable) contracts accrues each period at the benchmark rate in force on that period's date (benchmark + spread), honouring the contract's rate-change effective basis. When disabled (default), the contract's current rate is used for every period."
            checked={settings.reconstruct_historic_rates_variable === true || settings.reconstruct_historic_rates_variable === 'true'}
            onChange={handleChange('reconstruct_historic_rates_variable')}
          />
          <LabeledSwitch
            label="Reconstruct Historic Rates — Floating Contracts"
            caption="When enabled, full schedule generation for Floating contracts accrues each period at the benchmark rate in force on that period's date (benchmark + spread), honouring the contract's rate-change effective basis. When disabled (default), the contract's current rate is used for every period."
            checked={settings.reconstruct_historic_rates_floating === true || settings.reconstruct_historic_rates_floating === 'true'}
            onChange={handleChange('reconstruct_historic_rates_floating')}
          />
        </ToggleGroup>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={loadSettings} disabled={saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

export default TenantSettingsPage;
